import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import { FlowType, Step } from "@redotech/redo-model/return-flow";
import { isConditionStepValid } from "@redotech/redo-model/return-flow-step-valid";
import { Condition } from "@redotech/redo-model/return-flow/condition";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { assertNever } from "@redotech/util/type";
import { produce } from "immer";
import * as React from "react";
import { Dispatch, ReactElement, SetStateAction } from "react";
import { ASSIGNMENT_TYPE } from "../rules/conditions/assignment-type";
import { MESSAGE_BODY_MATCHES } from "../rules/conditions/message-body-matches";
import { SENT_FROM_EMAIL } from "../rules/conditions/sent-from-email";
import { SUBJECT_LINE } from "../rules/conditions/subject-line";
import { RECEIVED_AT_EMAIL } from "../rules/received-at-email";
import { TICKET_CHANNEL } from "../rules/ticket-channel";
import { TOPIC } from "../rules/topic";
import { StepId, StepType, StepTypeDetailsProps } from "../step";
import { ADJUSTMENT_AMOUNT } from "./condition-types/adjustment-amount";
import { AFTER_HOURS } from "./condition-types/after-hours";
import { CHANNEL } from "./condition-types/channel";
import { COLLECTION } from "./condition-types/collection";
import { COMPENSATION_METHOD } from "./condition-types/compensation-method";
import { COVERAGE } from "./condition-types/coverage";
import { CUSTOMER_ADDRESS } from "./condition-types/customer-address";
import { CUSTOMER_COUNTRY } from "./condition-types/customer-country";
import { CUSTOMER_EMAIL } from "./condition-types/customer-email";
import { CUSTOMER_EMAIL_SUBSCRIBER } from "./condition-types/customer-email-subscriber";
import { CUSTOMER_SMS_SUBSCRIBER } from "./condition-types/customer-sms-subscriber";
import { CUSTOMER_TAG } from "./condition-types/customer-tag";
import { DAMAGED } from "./condition-types/damaged";
import { ORDER_DATE, RETURN_DATE } from "./condition-types/date-conditions";
import { DELIVERY_WITHIN } from "./condition-types/delivery-within";
import { DISCOUNT } from "./condition-types/discount";
import { EXCHANGE_COUNT } from "./condition-types/exchange-count";
import { EXTENDED_WARRANTY } from "./condition-types/extended-warranty";
import { EXTENDED_WARRANTY_PURCHASED } from "./condition-types/extended-warranty-purchased";
import { FINAL_SALE_RETURN } from "./condition-types/final-sale-return";
import { FULFILLMENT_TRACKING_UPDATE_WITHIN } from "./condition-types/fulfillment-tracking-update-within";
import { FULFILLMENT_WITHIN } from "./condition-types/fulfillment-within";
import { IS_REPAIR } from "./condition-types/is-repair";
import { IS_REFUND_RETURN } from "./condition-types/is_refund_return";
import { ITEM_DISCOUNT } from "./condition-types/item-discount";
import { ITEMS_BEING_RETURNED_COUNT } from "./condition-types/items-being-returned-count";
import { LOCATION_NAME } from "./condition-types/location-name";
import { LOYALTY_EARNED } from "./condition-types/loyalty-earned";
import { MERCHANT_CREATED } from "./condition-types/merchant-created";
import { MULTIPLE_CHOICE } from "./condition-types/multiple-choice";
import { NON_RETURNED_VALUE } from "./condition-types/non-returned-value";
import { NUM_CUSTOMER_CLAIMS } from "./condition-types/num-customer-claims";
import { NUM_CUSTOMER_RETURNS } from "./condition-types/num-customer-returns";
import { NUM_ORDERS } from "./condition-types/num-orders";
import { ORDER_COUNT_GREATER_THAN } from "./condition-types/order-count_greater_than";
import { ORDER_NUMBER } from "./condition-types/order-number";
import { ORDER_TAG } from "./condition-types/order-tag";
import { ORDER_TIME_COLLECTION } from "./condition-types/order-time-collections";
import { ORDER_WITHIN } from "./condition-types/order-within";
import { ORDERLESS } from "./condition-types/orderless";
import { PACKAGE_PROTECTION } from "./condition-types/package-protection";
import { PAYMENT_METHOD } from "./condition-types/payment-method";
import { PLATFORM } from "./condition-types/platform";
import { PRICE } from "./condition-types/price";
import { PRODUCT_PROPERTY } from "./condition-types/product-property";
import { PRODUCT_TAG } from "./condition-types/product-tag";
import { PRODUCT_TYPE } from "./condition-types/product-type";
import { PRODUCT_VARIANT_METAFIELD } from "./condition-types/product-variant-metafield";
import { PURCHASE_CHANNEL } from "./condition-types/purchase-channel";
import { RETURN_METHOD } from "./condition-types/return-method";
import { RETURN_RATE } from "./condition-types/return-rate";
import { RETURN_REASON } from "./condition-types/return-reason";
import { RETURN_TYPE } from "./condition-types/return-type";
import { RETURN_VALUE } from "./condition-types/return-value";
import { SALES_CHANNEL } from "./condition-types/sales-channel";
import { SKU } from "./condition-types/sku";
import { TOTAL_PRE_DISCOUNT_PRICE } from "./condition-types/total-pre-discount-price";
import { TOTAL_RETURN_VALUE } from "./condition-types/total-return-value";
import { TOTAL_SPENT_GREATER_THAN } from "./condition-types/total-spent_greater_than";
import { TRACKING_TYPE } from "./condition-types/tracking-type";
import { VENDOR } from "./condition-types/vendor";
import { WRONG_PRODUCT } from "./condition-types/wrong-product";
export interface ConditionState {
  conditionType: ConditionType<any, any> | undefined;
  nextTrue?: StepId;
  nextFalse?: StepId;
  conditionState?: any;
  customTitle?: string;
}

export namespace ConditionState {
  export function valid(state: ConditionState, flowType: FlowType) {
    const validateResult = isConditionStepValid({
      conditionStep: { nextTrue: state.nextTrue, nextFalse: state.nextFalse },
      flowType,
      isConditionValid: () => {
        const valid =
          state.conditionType !== undefined &&
          state.conditionType.valid(state.conditionState);
        return valid ? { valid: true } : { valid: false, errors: [] };
      },
    });
    return validateResult.valid;
  }
}

export interface ConditionTypeDetailsProps<T> {
  state: T;
  setState: Dispatch<SetStateAction<T>>;
  disabled?: boolean;
  flowType: FlowType;
}

export interface ConditionType<T, M> {
  readonly empty: T;
  name: string;
  Details(props: ConditionTypeDetailsProps<T>): ReactElement | null;
  description(state: T): string;
  valid(state: T): boolean;
  fromModel(model: M): T;
  toModel(state: T): M;
}

const getType = (
  condition: Condition,
  getDetailsForCondition: (
    condition: Condition,
  ) => React.ComponentType<ConditionTypeDetailsProps<any>>,
): ConditionType<any, any> => {
  const Details = getDetailsForCondition(condition);
  switch (condition.type) {
    case Condition.COLLECTION:
      return COLLECTION(Details);
    case Condition.ORDER_TIME_COLLECTION:
      return ORDER_TIME_COLLECTION(Details);
    case Condition.COVERAGE:
      return COVERAGE(Details);
    case Condition.PACKAGE_PROTECTION:
      return PACKAGE_PROTECTION(Details);
    case Condition.CUSTOMER_COUNTRY:
      return CUSTOMER_COUNTRY(Details);
    case Condition.CUSTOMER_ADDRESS:
      return CUSTOMER_ADDRESS(Details);
    case Condition.CUSTOMER_TAG:
      return CUSTOMER_TAG(Details);
    case Condition.CUSTOMER_EMAIL:
      return CUSTOMER_EMAIL(Details);
    case Condition.ORDER_NUMBER:
      return ORDER_NUMBER(Details);
    case Condition.DISCOUNT:
      return DISCOUNT(Details);
    case Condition.ITEM_DISCOUNT:
      return ITEM_DISCOUNT(Details);
    case Condition.LOYALTY_EARNED:
      return LOYALTY_EARNED(Details);
    case Condition.EXCHANGE_COUNT:
      return EXCHANGE_COUNT(Details);
    case Condition.ITEMS_BEING_RETURNED_COUNT:
      return ITEMS_BEING_RETURNED_COUNT(Details);
    case Condition.ADJUSTMENT_AMOUNT:
      return ADJUSTMENT_AMOUNT(Details);
    case Condition.RETURN_METHOD:
      return RETURN_METHOD(Details);
    case Condition.NUM_CUSTOMER_CLAIMS:
      return NUM_CUSTOMER_CLAIMS(Details);
    case Condition.NUM_CUSTOMER_RETURNS:
      return NUM_CUSTOMER_RETURNS(Details);
    case Condition.NUM_ORDERS:
      return NUM_ORDERS(Details);
    case Condition.EXTENDED_WARRANTY:
      return EXTENDED_WARRANTY(Details);
    case Condition.EXTENDED_WARRANTY_PURCHASED:
      return EXTENDED_WARRANTY_PURCHASED(Details);
    case Condition.RETURN_RATE:
      return RETURN_RATE(Details);
    case Condition.ORDER_DATE:
      return ORDER_DATE(Details);
    case Condition.ORDER_TAG:
      return ORDER_TAG(Details);
    case Condition.PAYMENT_METHOD:
      return PAYMENT_METHOD(Details);
    case Condition.PLATFORM:
      return PLATFORM(Details);
    case Condition.PRICE:
      return PRICE(Details);
    case Condition.RETURN_VALUE:
      return RETURN_VALUE(Details);
    case Condition.PRODUCT_PROPERTY:
      return PRODUCT_PROPERTY(Details);
    case Condition.PRODUCT_TAG:
      return PRODUCT_TAG(Details);
    case Condition.PRODUCT_TYPE:
      return PRODUCT_TYPE(Details);
    case Condition.PRODUCT_VARIANT_METAFIELD:
      return PRODUCT_VARIANT_METAFIELD(Details);
    case Condition.RETURN_DATE:
      return RETURN_DATE(Details);
    case Condition.SALES_CHANNEL:
      return SALES_CHANNEL(Details);
    case Condition.SKU:
      return SKU(Details);
    case Condition.DELIVERY_WITHIN:
      return DELIVERY_WITHIN(Details);
    case Condition.FULFILLMENT_WITHIN:
      return FULFILLMENT_WITHIN(Details);
    case Condition.FINAL_SALE_RETURN:
      return FINAL_SALE_RETURN(Details);
    case Condition.ORDERLESS:
      return ORDERLESS(Details);
    case Condition.ORDER_WITHIN:
      return ORDER_WITHIN(Details);
    case Condition.AFTER_HOURS:
      return AFTER_HOURS(Details);
    case Condition.FULFILLMENT_TRACKING_UPDATE_WITHIN:
      return FULFILLMENT_TRACKING_UPDATE_WITHIN(Details);
    case Condition.COMPENSATION_METHOD:
      return COMPENSATION_METHOD(Details);
    case Condition.CUSTOMER_EMAIL_SUBSCRIBER:
      return CUSTOMER_EMAIL_SUBSCRIBER(Details);
    case Condition.CUSTOMER_SMS_SUBSCRIBER:
      return CUSTOMER_SMS_SUBSCRIBER(Details);
    case Condition.TICKET_CHANNEL:
      return TICKET_CHANNEL(Details);
    case Condition.TOPIC:
      return TOPIC(Details);
    case Condition.RECEIVED_AT_EMAIL:
      return RECEIVED_AT_EMAIL(Details);
    case Condition.SENT_FROM_EMAIL:
      return SENT_FROM_EMAIL(Details);
    case Condition.SUBJECT_LINE:
      return SUBJECT_LINE(Details);
    case Condition.MESSAGE_BODY_MATCHES:
      return MESSAGE_BODY_MATCHES(Details);
    case Condition.ORDER_COUNT_GREATER_THAN:
      return ORDER_COUNT_GREATER_THAN(Details);
    case Condition.TOTAL_SPENT_GREATER_THAN:
      return TOTAL_SPENT_GREATER_THAN(Details);
    case Condition.VENDOR:
      return VENDOR(Details);
    case Condition.TRACKING_TYPE:
      return TRACKING_TYPE(Details);
    case Condition.IS_REFUND_RETURN:
      return IS_REFUND_RETURN(Details);
    case Condition.PURCHASE_CHANNEL:
      return PURCHASE_CHANNEL(Details);
    case Condition.DAMAGED:
      return DAMAGED(Details);
    case Condition.WRONG_PRODUCT:
      return WRONG_PRODUCT(Details);
    case Condition.RETURN_REASON:
      return RETURN_REASON(Details);
    case Condition.ASSIGNMENT_TYPE:
      return ASSIGNMENT_TYPE(Details);
    case Condition.NON_RETURNED_VALUE:
      return NON_RETURNED_VALUE(Details);
    case Condition.CHANNEL:
      return CHANNEL(Details);
    case Condition.MERCHANT_CREATED:
      return MERCHANT_CREATED(Details);
    case Condition.TOTAL_RETURN_VALUE:
      return TOTAL_RETURN_VALUE(Details);
    case Condition.TOTAL_PRE_DISCOUNT_PRICE:
      return TOTAL_PRE_DISCOUNT_PRICE(Details);
    case Condition.RETURN_TYPE:
      return RETURN_TYPE(Details);
    case Condition.IS_REPAIR:
      return IS_REPAIR(Details);
    case Condition.LOCATION_NAME:
      return LOCATION_NAME(Details);
    case Condition.MULTIPLE_CHOICE:
      return MULTIPLE_CHOICE(Details);
    default:
      assertNever(condition);
  }
};

export const CONDITION = (
  Details: React.ComponentType<StepTypeDetailsProps<ConditionState>>,
  getDetailsForCondition: (
    condition: Condition,
  ) => React.ComponentType<ConditionTypeDetailsProps<any>>,
): StepType<ConditionState, Step.Condition> => ({
  Details(props: StepTypeDetailsProps<ConditionState>) {
    return React.createElement(Details, props);
  },
  customTitle(state) {
    return state.customTitle;
  },
  Icon: AlertCircleIcon,
  description(state) {
    return state.conditionType
      ? state.conditionType.description(state.conditionState)
      : "";
  },
  downstream(state) {
    // Always return items at their semantic positions to match handle indices
    // Index 0 = left handle (True), Index 1 = right handle (False)
    return [
      { id: state.nextTrue, label: "True" },
      { id: state.nextFalse, label: "False" },
    ];
  },
  maxDownstream() {
    return 2;
  },
  empty: {
    conditionType: undefined,
    nextFalse: undefined,
    nextTrue: undefined,
  },
  fromModel(model, id) {
    const type = getType(model.condition, getDetailsForCondition);
    return {
      customTitle: model.customTitle,
      conditionType: type,
      nextFalse: model.nextFalse ? id(model.nextFalse) : undefined,
      nextTrue: model.nextTrue ? id(model.nextTrue) : undefined,
      conditionState: type.fromModel(model.condition),
    };
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.nextFalse === stepId) {
        state.nextFalse = undefined;
      }
      if (state.nextTrue === stepId) {
        state.nextTrue = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextTrue = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextFalse = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for Condition step`,
        );
      }
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.nextTrue === targetStepId) {
        draft.nextTrue = undefined;
      }
      if (draft.nextFalse === targetStepId) {
        draft.nextFalse = undefined;
      }
    });
  },
  title: "Condition",
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: Step.CONDITION,
      condition: state.conditionType!.toModel(state.conditionState),
      nextTrue: state.nextTrue ? id(state.nextTrue) : undefined,
      nextFalse: state.nextFalse ? id(state.nextFalse!) : undefined,
    };
  },
  valid: ConditionState.valid,
});
